import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { uiPure } from '@mona/shared/utils';
import { UiDynamicElementConfig, UiDynamicType } from '../../../forms';
import { UiTableCellValue, UiTableColumn, UiTableRow } from '../../models';
import { integerValidator } from './edit-cell-value-dialog.helpers';

/** Show errors after touch/dirty or Save click */
class SubmitErrorStateMatcher implements ErrorStateMatcher {
    constructor(private readonly getSubmitted: () => boolean) {}

    isErrorState(control: FormControl | null, _form: FormGroupDirective | NgForm | null): boolean {
        return !!(control && control.invalid && (control.dirty || control.touched || this.getSubmitted()));
    }
}

/**
 * Data passed to the edit cell value dialog
 */
export interface EditCellValueDialogData {
    /** Row data */
    row: UiTableRow;
    /** Column data */
    column: UiTableColumn;
    /** Current cell value */
    cell: UiTableCellValue;
    /** Row index */
    rowIdx: number;
    /** Column index */
    colIdx: number;
    /** Form configuration for the cell edit */
    formConfig: UiDynamicElementConfig[];
    /** Whether the dialog is in view-only mode */
    viewOnly?: boolean;
}

/** Precomputed field for template (no method calls in CD) */
export interface EditCellFieldView {
    key: string;
    index: number;
    isBoolean: boolean;
    inputDataType: 'number' | 'text';
    /** Input label or checkbox name */
    label: string;
    /** Direct control ref — avoids formControlName breaking on keys like "value.0" */
    control: FormControl;
}

@Component({
    selector: 'ui-edit-cell-value-dialog',
    templateUrl: './edit-cell-value-dialog.component.html',
    styleUrls: ['./edit-cell-value-dialog.component.scss'],
    standalone: false,
})
export class EditCellValueDialogComponent implements AfterViewInit, OnInit, OnDestroy {
    /** Form group containing value controls */
    valueForm!: FormGroup;

    /** Date control for the datepicker (readonly) */
    dateControl = new FormControl({ value: null, disabled: true });

    /** Whether this is a GROUP type (multiple inputs) */
    isGroupType = false;

    /** Precomputed fields for template */
    fields: EditCellFieldView[] = [];

    /** Whether the dialog is in view-only mode */
    viewOnly = false;

    /** Whether the form value has changed from initial */
    isValueChanged = false;

    /** Whether the cell already has a documented value */
    hasExistingValue = false;

    /** Whether Save was clicked, used to show validation errors */
    submitted = false;

    /** Error state matcher for validation display */
    errorMatcher = new SubmitErrorStateMatcher(() => this.submitted);

    /** Subtitle text showing units */
    subtitleText = '';

    /** Subject for cleanup */
    private destroy$ = new Subject<void>();

    /** Initial form value for change detection */
    private initialFormValue: Record<string, unknown> = {};

    /**
     * Constructor
     * @param dialogRef Reference to the dialog
     * @param elementRef Element reference for focusing
     * @param data Dialog data containing cell context
     */
    constructor(
        private dialogRef: MatDialogRef<EditCellValueDialogComponent>,
        private elementRef: ElementRef,
        @Inject(MAT_DIALOG_DATA) public data: EditCellValueDialogData,
    ) {
        this.viewOnly = data.viewOnly ?? false;
        this.initializeForm();
        this.initialFormValue = this.valueForm.getRawValue();
        this.hasExistingValue = this.detectExistingValue();
        this.dateControl.setValue(data.column?.meta?.date || new Date());
        this.subtitleText = this.buildSubtitleText();
    }

    /** Edit needs a change. New values can always be saved. */
    get isSaveDisabled(): boolean {
        if (this.viewOnly) {
            return true;
        }
        return this.hasExistingValue && !this.isValueChanged;
    }

    /**
     * Initialize form controls from the cell configuration.
     */
    private initializeForm(): void {
        const controls: { [key: string]: FormControl } = {};
        this.isGroupType = this.data.formConfig.some(c => /\.\d+$/.test(c.name));
        this.fields = [];

        if (this.isGroupType) {
            this.data.formConfig.forEach(config => {
                const match = config.name.match(/\.(\d+)$/);
                if (!match) {
                    return;
                }
                const index = +match[1];
                const dataType = (this.data.row?.children?.[index]?.dataType || '').toLowerCase();
                const initial = Array.isArray(this.data.cell?.value) ? this.data.cell.value[index] : null;
                this.addControl(controls, config, dataType, initial, index);
            });
        } else {
            const config = this.data.formConfig[0];
            if (config) {
                const isBoolean = config.type === UiDynamicType.Boolean;
                const dataType = isBoolean
                    ? 'boolean'
                    : (this.data.row?.dataType || String(config.type || 'text')).toLowerCase();
                const initial = this.data.cell?.[config.name] ?? this.data.cell?.value ?? null;
                this.addControl(controls, config, dataType, initial, 0);
            }
        }

        this.valueForm = new FormGroup(controls);
        if (this.viewOnly) {
            Object.values(controls).forEach(c => c.disable());
        }
    }

    /**
     * Add one form control with validation matching its data type.
     * @param controls Dialog controls
     * @param config Field configuration
     * @param dataType Child or row data type
     * @param initial Initial cell value
     * @param index Field index
     */
    private addControl(
        controls: { [key: string]: FormControl },
        config: UiDynamicElementConfig,
        dataType: string,
        initial: unknown,
        index: number,
    ): void {
        const isBoolean = dataType === 'boolean';
        const validators = [];
        if (!isBoolean) {
            validators.push(Validators.required);
            if (config.maxLength) {
                validators.push(Validators.maxLength(config.maxLength));
            }
            if (dataType === 'integer') {
                validators.push(integerValidator);
            }
        }

        const boolValue = initial === true || String(initial).toLowerCase() === 'true';
        const control = new FormControl(isBoolean ? boolValue : initial, validators);
        controls[config.name] = control;
        this.fields.push({
            key: config.name,
            index,
            isBoolean,
            // integer stays text: matInputCommaDot adds decimal `pattern` on data-type=number (wrong msg vs notInteger)
            inputDataType:
                dataType !== 'integer' && ['decimal', 'quantity', 'number'].includes(dataType) ? 'number' : 'text',
            label: isBoolean ? this.getBooleanName(index) : this.getInputLabel(this.data.row, index),
            control,
        });
    }

    /**
     * Checkbox shows parameter/child name only (no true/false text).
     * @param index Child index
     */
    private getBooleanName(index: number): string {
        if (this.isGroupType) {
            return this.data.row?.children?.[index]?.name || '';
        }
        return this.data.row?.name || '';
    }

    /**
     * Check whether the cell already has a documented value.
     */
    private detectExistingValue(): boolean {
        const v = this.data.cell?.value;
        if (this.isGroupType) {
            return Array.isArray(v) && v.some(x => x !== null && x !== undefined && x !== '');
        }
        return typeof v === 'boolean' || (v !== null && v !== undefined && v !== '');
    }

    ngOnInit(): void {
        this.valueForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.isValueChanged =
                JSON.stringify(this.valueForm.getRawValue()) !== JSON.stringify(this.initialFormValue);
        });
    }

    /**
     * Focus the first editable input after the dialog opens.
     */
    ngAfterViewInit(): void {
        this.dialogRef
            .afterOpened()
            .pipe(take(1))
            .subscribe(() => {
                setTimeout(() => {
                    this.elementRef.nativeElement.querySelector('input[type="text"], input[type="number"]')?.focus();
                }, 0);
            });
    }

    /**
     * Validate and submit form values.
     */
    onFormSubmit(): void {
        if (this.viewOnly || this.isSaveDisabled) {
            return;
        }
        this.submitted = true;
        this.valueForm.markAllAsTouched();
        if (this.valueForm.valid) {
            this.dialogRef.close({ action: 'submit', value: this.getSubmitValue() });
        }
    }

    /**
     * Close dialog without saving.
     */
    onCancel(): void {
        this.dialogRef.close(null);
    }

    /**
     * Get label for the input
     * @param row UiTableRow
     * @param index Index of the child
     */
    @uiPure
    getInputLabel(row: UiTableRow, index: number): string {
        return row.children?.length
            ? `${row.children[index]?.name || ''} (${row.children[index]?.unit || ''})`
            : row?.name || '';
    }

    /**
     * Normalize booleans to lowercase "true"/"false" before emit.
     */
    private getSubmitValue(): Record<string, unknown> {
        const raw = { ...this.valueForm.getRawValue() };
        this.fields.forEach(field => {
            if (field.isBoolean) {
                raw[field.key] = field.control.value === true ? 'true' : 'false';
            }
        });
        return raw;
    }

    /**
     * Build subtitle text showing units.
     */
    private buildSubtitleText(): string {
        if (this.isGroupType && this.data.row?.children?.length) {
            return this.data.row.children
                .map(c => c.unit)
                .filter(Boolean)
                .join('/');
        }
        return this.data.row?.unit || '';
    }

    /**
     * Clean up subscriptions.
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
